import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Panel, StatusBadge, Empty } from "./ui";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, PackageCheck } from "lucide-react";

export const downloadTransferNote = async (t) => {
  try {
    const res = await api.get(`/transfers/${t.id}/note.pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `netstar_transfer_${t.transfer_no}.pdf`; a.click();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error("Could not generate transfer note"); }
};

const ReceiveDialog = ({ t, onClose, onDone }) => {
  const outstanding = t.serials.filter((s) => !(t.received_serials || []).includes(s));
  const [sel, setSel] = useState(outstanding);
  const [scan, setScan] = useState("");
  const [scanned, setScanned] = useState([]);
  const toggle = (s) => setSel((p) => (p.includes(s) ? p.filter((x) => x !== s) : [...p, s]));

  const onScan = (e) => {
    if (e.key !== "Enter" && e.key !== "Tab") return;
    e.preventDefault();
    const s = scan.trim(); setScan("");
    if (!s) return;
    if (!outstanding.includes(s)) { toast.error(`${s} is not outstanding on this transfer`); return; }
    setScanned((p) => {
      const next = p.includes(s) ? p : [...p, s];
      setSel(next);
      return next;
    });
  };

  const submit = async () => {
    if (sel.length === 0) { toast.error("Select at least one unit"); return; }
    try {
      const { data } = await api.post(`/transfers/${t.id}/receive`, { serials: sel });
      toast.success(`${data.received.length} unit(s) received${data.outstanding.length ? ` · ${data.outstanding.length} still in transit` : " · transfer complete"}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not confirm receipt"); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="transfer-receive-dialog">
        <DialogHeader><DialogTitle className="font-head">Confirm receipt · {t.transfer_no}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500">Check each unit against the transfer note. Scanning switches to "scanned only" — unscanned units stay in transit.</p>
        <Input data-testid="transfer-receive-scan" autoFocus value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={onScan}
          placeholder="Scan a serial…" className="rounded-sm font-label" />
        <div className="flex items-center justify-between text-xs font-head">
          <span data-testid="transfer-receive-count"><b className="text-[#00857C]">{sel.length}</b> of {outstanding.length} selected</span>
          <span className="flex gap-3">
            <button data-testid="transfer-receive-all" className="text-[#00857C] hover:underline" onClick={() => setSel(outstanding)}>All</button>
            <button data-testid="transfer-receive-none" className="text-slate-500 hover:underline" onClick={() => { setSel([]); setScanned([]); }}>None</button>
          </span>
        </div>
        <div className="max-h-64 overflow-y-auto border border-slate-100 rounded-sm divide-y divide-slate-50">
          {outstanding.map((s) => (
            <label key={s} className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer">
              <input type="checkbox" data-testid={`transfer-receive-chk-${s}`} checked={sel.includes(s)} onChange={() => toggle(s)} />
              <span className="font-label">{s}</span>
              {scanned.includes(s) && <span className="ml-auto text-[10px] font-bold uppercase text-[#00857C]">Scanned</span>}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
          <Button data-testid="transfer-receive-submit" onClick={submit} className="rounded-sm bg-[#00857C] hover:bg-[#00a89d] font-head">Confirm receipt ({sel.length})</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Transfers() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [recv, setRecv] = useState(null);
  const canReceive = ["regional_manager", "warehouse_manager", "operations_controller", "system_admin"].includes(user.role);
  const load = useCallback(() => api.get("/transfers").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Regional Transfers" subtitle="Central bin → regional warehouse consignments. Stock stays in transit until the regional confirms receipt." />
      <Panel title="Transfers">
        {rows.length === 0 ? <Empty>No transfers yet.</Empty> : (
          <table className="w-full text-sm" data-testid="transfers-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Note</th><th className="py-2 pr-3">To</th><th className="py-2 pr-3">Units</th><th className="py-2 pr-3">Status</th><th className="py-2 pr-3">Dispatched</th><th></th></tr></thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} data-testid={`transfer-row-${t.id}`} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-label text-[13px] text-[#0B2545]">{t.transfer_no}</td>
                  <td className="py-2 pr-3 font-medium text-[#0B2545]">{t.to_name}<div className="text-xs text-slate-400">{t.region}</div></td>
                  <td className="py-2 pr-3">{(t.received_serials || []).length} / {t.serials.length} received</td>
                  <td className="py-2 pr-3"><StatusBadge status={t.status} /></td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{new Date(t.dispatched_at).toLocaleString()}<div>{t.dispatched_by}</div></td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {t.status !== "received" && canReceive && (
                      <Button data-testid={`transfer-receive-${t.id}`} onClick={() => setRecv(t)} size="sm" className="rounded-sm bg-[#ca8a04] hover:bg-[#ca8a04]/90 h-7 text-xs gap-1">
                        <PackageCheck className="h-3.5 w-3.5" /> Confirm receipt
                      </Button>
                    )}
                    <Button data-testid={`transfer-note-${t.id}`} onClick={() => downloadTransferNote(t)} size="sm" variant="outline" className="rounded-sm border-slate-300 h-7 text-xs gap-1 ml-2">
                      <FileText className="h-3.5 w-3.5" /> Transfer note
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>
      {recv && <ReceiveDialog t={recv} onClose={() => setRecv(null)} onDone={() => { setRecv(null); load(); }} />}
    </div>
  );
}
